'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/src/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/src/components/ui/card';
import { Badge } from '@/src/components/ui/badge';
import { Input } from '@/src/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/src/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/src/components/ui/dropdown-menu';
import { Loader2, Plus, MessageSquare, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageTitle } from '@/src/components/page-title';

interface ThreadSummary {
  thread_id: string;
  title: string;
  updated_at: number;
  last_job_status: string | null;
}

function StatusBadge({ status }: { status: string | null }) {
  if (status === 'pending' || status === 'running') {
    return (
      <Badge variant='secondary' className='gap-1 shrink-0'>
        <Loader2 className='h-3 w-3 animate-spin' />
        Generating
      </Badge>
    );
  }
  if (status === 'failed' || status === 'dead') {
    return (
      <Badge variant='destructive' className='shrink-0'>
        Failed
      </Badge>
    );
  }
  return null;
}

export default function MakerThreadsPage() {
  const router = useRouter();
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [renameTarget, setRenameTarget] = useState<ThreadSummary | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<ThreadSummary | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/maker/threads');
      if (res.ok) {
        const data = await res.json();
        setThreads(data.threads);
      }
    } catch {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  // Any in-flight job's status won't update itself in this list — poll while at least one
  // thread is still pending/running, same interval as the chat page's own job poll.
  useEffect(() => {
    const hasActiveJob = threads.some(
      (t) => t.last_job_status === 'pending' || t.last_job_status === 'running'
    );
    if (!hasActiveJob) return;
    const interval = setInterval(loadThreads, 2000);
    return () => clearInterval(interval);
  }, [threads, loadThreads]);

  const startNewChat = async () => {
    setCreating(true);
    try {
      const res = await fetch('/api/v1/maker/threads', { method: 'POST' });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      router.push(`/maker/${data.thread_id}`);
    } catch {
      toast.error('Could not start a new conversation');
      setCreating(false);
    }
  };

  const openRename = (t: ThreadSummary) => {
    setRenameTarget(t);
    setRenameValue(t.title);
  };

  const submitRename = async () => {
    if (!renameTarget) return;
    const title = renameValue.trim();
    if (!title) {
      toast.error('Title cannot be empty');
      return;
    }
    setRenaming(true);
    try {
      const res = await fetch(`/api/v1/maker/threads/${renameTarget.thread_id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ title })
      });
      if (!res.ok) throw new Error('failed');
      const updated = await res.json();
      setThreads((prev) =>
        prev.map((t) => (t.thread_id === updated.thread_id ? { ...t, title: updated.title } : t))
      );
      setRenameTarget(null);
    } catch {
      toast.error('Could not rename conversation');
    } finally {
      setRenaming(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/v1/maker/threads/${deleteTarget.thread_id}`, {
        method: 'DELETE'
      });
      if (!res.ok && res.status !== 204) throw new Error('failed');
      setThreads((prev) => prev.filter((t) => t.thread_id !== deleteTarget.thread_id));
      setDeleteTarget(null);
    } catch {
      toast.error('Could not delete conversation');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className='max-w-3xl mx-auto p-4'>
      <PageTitle title='Course Maker Assistant' />
      <div className='flex items-center justify-between mb-4'>
        <h1 className='text-xl font-semibold'>Course Maker Assistant</h1>
        <Button onClick={startNewChat} disabled={creating}>
          {creating ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <Plus className='h-4 w-4' />
          )}
          New chat
        </Button>
      </div>

      {loading ? (
        <div className='flex justify-center py-12'>
          <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
        </div>
      ) : threads.length === 0 ? (
        <p className='text-base text-muted-foreground text-center py-12'>
          No conversations yet — start one to create a course, lesson, quiz, or flashcard deck.
        </p>
      ) : (
        <div className='space-y-2'>
          {threads.map((t) => (
            <Card key={t.thread_id} className='hover:bg-muted/30 transition-colors'>
              <CardHeader className='flex-row items-center gap-3 py-3'>
                <div
                  className='flex flex-1 min-w-0 items-center gap-3 cursor-pointer'
                  onClick={() => router.push(`/maker/${t.thread_id}`)}
                >
                  <MessageSquare className='h-4 w-4 text-muted-foreground shrink-0' />
                  <CardTitle className='text-base font-normal truncate flex-1'>
                    {t.title}
                  </CardTitle>
                  <StatusBadge status={t.last_job_status} />
                  <span className='text-xs text-muted-foreground shrink-0 hidden sm:inline'>
                    {formatDistanceToNow(t.updated_at * 1000, { addSuffix: true })}
                  </span>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-8 w-8 shrink-0'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className='h-4 w-4' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuItem onClick={() => openRename(t)}>
                      <Pencil className='h-4 w-4 mr-2' />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setDeleteTarget(t)}
                      className='text-destructive focus:text-destructive'
                    >
                      <Trash2 className='h-4 w-4 mr-2' />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!renameTarget} onOpenChange={(open) => !open && setRenameTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename conversation</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            maxLength={60}
            onKeyDown={(e) => e.key === 'Enter' && submitRename()}
            autoFocus
          />
          <DialogFooter>
            <Button variant='ghost' onClick={() => setRenameTarget(null)} disabled={renaming}>
              Cancel
            </Button>
            <Button onClick={submitRename} disabled={renaming}>
              {renaming && <Loader2 className='h-4 w-4 animate-spin mr-2' />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete conversation?</DialogTitle>
          </DialogHeader>
          <p className='text-base text-muted-foreground'>
            &ldquo;{deleteTarget?.title}&rdquo; will be permanently deleted. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant='ghost' onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant='destructive' onClick={confirmDelete} disabled={deleting}>
              {deleting && <Loader2 className='h-4 w-4 animate-spin mr-2' />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
